import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FaMoneyBillWave, FaCreditCard, FaMobileAlt, FaTrash, FaPlus, FaCheckCircle } from 'react-icons/fa';

const MultiplePaymentModal = ({
  show,
  onHide,
  onConfirm,
  totalAmount,
  currency = 'UZS',
  paymentTypes = [
    { value: 'cash', label: 'Naqd pul' },
    { value: 'terminal', label: 'Terminal' },
    { value: 'card', label: 'Karta' },

  ]
}) => {
  const [payments, setPayments] = useState([]);
  const [amountInput, setAmountInput] = useState('');
  const [selectedMethod, setSelectedMethod] = useState('cash');
  const [error, setError] = useState('');

  const totalPaid = payments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
  const remaining = totalAmount - totalPaid;

  useEffect(() => {
    if (show) {
      setPayments([]);
      setAmountInput('');
      setSelectedMethod('cash');
      setError('');
    }
  }, [show, totalAmount]);

  // Update selected method if the current one is added
  useEffect(() => {
    const usedMethods = payments.map(p => p.method);
    if (usedMethods.includes(selectedMethod)) {
      const available = paymentTypes.find(t => !usedMethods.includes(t.value));
      if (available) {
        setSelectedMethod(available.value);
      } else {
        setSelectedMethod(null);
      }
    }
  }, [payments, selectedMethod, paymentTypes]);

  const handleAddPayment = () => {
    const amount = Number(amountInput);
    if (!amountInput || isNaN(amount) || amount <= 0) {
      toast.error('Iltimos, haqiqiy miqdorni kiriting');
      return;
    }
    if (amount > remaining + 0.01) {
      toast.error('Miqdor qolgan balansdan oshib ketdi');
      return;
    }
    if (!selectedMethod) {
      toast.error('To\'lov usulini tanlang');
      return;
    }

    const newPayment = {
      method: selectedMethod,
      amount: amount,
      currency: currency
    };

    const newPayments = [...payments, newPayment];
    setPayments(newPayments);

    const newRemaining = remaining - amount;
    if (newRemaining > 0) {
      setAmountInput(newRemaining.toString());
    } else {
      setAmountInput('');
    }
  };

  const removePayment = (index) => {
    const removedPayment = payments[index];
    const newPayments = payments.filter((_, i) => i !== index);
    setPayments(newPayments);
    // When removing, we can optionally update the amount input to the new remaining
    const newRemaining = remaining + (Number(removedPayment.amount) || 0);
    setAmountInput(newRemaining.toString());
  };

  const handleConfirm = () => {
    if (payments.length === 0) {
      setError('Kamida bitta to\'lov usuli qo\'shing');
      return;
    }

    if (Math.abs(remaining) > 0.01) {
      setError(`To'lov miqdori to'liq emas. Qoldi: ${remaining.toLocaleString()} ${currency}`);
      return;
    }

    onConfirm(payments);
    onHide();
  };

  const handleCancel = () => {
    onHide();
  };

  const methodIcons = {
    cash: FaMoneyBillWave,
    terminal: FaCreditCard,
    card: FaMobileAlt
  };

  const methodColors = {
    cash: '#28a745',
    terminal: '#007bff',
    card: '#17a2b8'
  };

  return (
    <Modal show={show} onHide={handleCancel} size="md" centered className="multiple-payment-modal">
      <Modal.Header closeButton className="border-bottom-0 pb-0">
        <Modal.Title className="fw-bold text-dark">Gibrid to'lov</Modal.Title>
      </Modal.Header>
      <Modal.Body className="pt-3">
        {error && <Alert variant="danger" className="py-2 small mb-3">{error}</Alert>}

        {/* Totals Display */}
        <div className="bg-light p-3 rounded-4 mb-4 border shadow-sm">
          <div className="d-flex justify-content-between text-muted small mb-1">
            <span>Jami summa:</span>
            <span className="fw-medium">{totalAmount.toLocaleString()} {currency}</span>
          </div>
          <div className="d-flex justify-content-between align-items-center border-top pt-2">
            <span className="fw-bold text-dark">Qoldi:</span>
            <span className={`h5 mb-0 fw-bold ${remaining > 0.01 ? 'text-danger' : 'text-success'}`}>
              {remaining.toLocaleString()} {currency}
            </span>
          </div>
        </div>

        {/* Method Selection Grid */}
        <div className="mb-3">
          <label className="form-label small fw-bold text-muted mb-2">To'lov usulini tanlang</label>
          <div className="row g-2">
            {paymentTypes.map((type) => {
              const Icon = methodIcons[type.value] || FaMoneyBillWave;
              const isUsed = payments.some(p => p.method === type.value);
              const isSelected = selectedMethod === type.value;
              const color = methodColors[type.value] || '#6c757d';

              return (
                <div key={type.value} className="col-4">
                  <button
                    onClick={() => !isUsed && setSelectedMethod(type.value)}
                    disabled={isUsed}
                    className={`w-100 p-2 rounded-3 border d-flex flex-column align-items-center transition-all ${isSelected ? 'border-primary bg-primary bg-opacity-10 shadow-sm' : 'border-light bg-white h-100'
                      } ${isUsed ? 'opacity-50 cursor-not-allowed bg-light' : ''}`}
                    style={{
                      minHeight: '70px',
                      transition: 'all 0.2s',
                      borderColor: isSelected ? color : '#dee2e6'
                    }}
                  >
                    <Icon size={20} color={isUsed ? '#adb5bd' : (isSelected ? color : '#6c757d')} className="mb-1" />
                    <span className="small fw-semibold text-center" style={{ fontSize: '11px', color: isUsed ? '#adb5bd' : (isSelected ? color : '#495057') }}>
                      {type.label}
                    </span>
                    {isUsed && <FaCheckCircle className="position-absolute top-0 end-0 m-1 text-success" size={12} />}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Amount Input */}
        <div className="row g-2 align-items-end mb-4">
          <div className="col">
            <label className="form-label small fw-bold text-muted mb-1">Miqdor</label>
            <div className="input-group">
              <span className="input-group-text bg-white border-end-0 text-muted">
                {currency === 'UZS' ? 'UZS' : '$'}
              </span>
              <Form.Control
                type="number"
                value={amountInput}
                onChange={(e) => setAmountInput(e.target.value)}
                placeholder="0"
                className="border-start-0 ps-0 shadow-none border-secondary-subtle"
                min="0"
                step="any"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddPayment();
                  }
                }}
              />
            </div>
          </div>
          <div className="col-auto">
            <Button
              variant="primary"
              onClick={handleAddPayment}
              disabled={remaining <= 0 || !selectedMethod}
              className="d-flex align-items-center gap-2 px-3 fw-bold"
              style={{ height: '38px' }}
            >
              <FaPlus size={14} /> Qo'shish
            </Button>
          </div>
        </div>


        {/* Added Payments List */}
        <div className="border-top pt-3">
          <label className="form-label small fw-bold text-muted mb-2">Qo'shilgan to'lovlar</label>
          <div className="payments-list" style={{ maxHeight: '180px', overflowY: 'auto' }}>
            {payments.length === 0 ? (
              <div className="text-center py-4 bg-light rounded-3 border-dashed">
                <span className="text-muted small italic">Hali to'lov qo'shilmadi</span>
              </div>
            ) : (
              <div className="d-flex flex-column gap-2">
                {payments.map((p, index) => {
                  const Icon = methodIcons[p.method] || FaMoneyBillWave;
                  const label = paymentTypes.find(t => t.value === p.method)?.label || p.method;
                  const color = methodColors[p.method] || '#6c757d';

                  return (
                    <div key={index} className="d-flex align-items-center justify-content-between p-2 rounded-3 border bg-white shadow-sm hover-shadow transition-all">
                      <div className="d-flex align-items-center gap-3">
                        <div className="p-2 rounded-circle bg-light" style={{ color }}>
                          <Icon size={16} />
                        </div>
                        <div>
                          <div className="fw-bold small">{label}</div>
                          <div className="text-muted" style={{ fontSize: '10px' }}>{currency}</div>
                        </div>
                      </div>
                      <div className="d-flex align-items-center gap-3">
                        <div className="fw-bold text-dark">{p.amount.toLocaleString()}</div>
                        <button
                          onClick={() => removePayment(index)}
                          className="btn btn-sm btn-link text-danger p-1 shadow-none"
                        >
                          <FaTrash size={14} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="border-top-0 pt-0">
        <Button variant="light" onClick={handleCancel} className="fw-medium px-4">
          Bekor qilish
        </Button>
        <Button
          variant="success"
          onClick={handleConfirm}
          disabled={Math.abs(remaining) > 0.01}
          className="fw-bold px-4 shadow-sm"
        >
          Tasdiqlash
        </Button>
      </Modal.Footer>

      <style>{`
        .multiple-payment-modal .modal-content {
          border-radius: 1.25rem;
          border: none;
          box-shadow: 0 10px 25px rgba(0,0,0,0.1);
        }
        .border-dashed {
          border: 2px dashed #dee2e6 !important;
        }
        .hover-shadow:hover {
          box-shadow: 0 4px 8px rgba(0,0,0,0.05) !important;
        }
        .cursor-pointer {
          cursor: pointer;
        }
        .input-group-text {
          font-size: 0.8rem;
          font-weight: bold;
        }
      `}</style>
    </Modal>
  );
};

export default MultiplePaymentModal;
